import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { AuthGuard } from '@/components/guards/AuthGuard';
import { RoleGuard } from '@/components/guards/RoleGuard';
import { GuestGuard } from '@/components/guards/GuestGuard';
import { PublicGuard } from '@/components/guards/PublicGuard';
import { AppLayout } from '@/components/layout/AppLayout';
import { LazyWrapper } from '@/components/common/LazyWrapper';
import { UserRole } from '@/types/user.types';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { MainLayout } from '@/components/layout/MainLayout';
import { MarketLayout } from '@/components/layout/MarketLayout';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/customer/HomePage'));
const FavouritesPage = lazy(() => import('@/pages/customer/FavouritesPage'));
const StoreHomePage = lazy(() => import('@/pages/StoreHomePage'));
const PublicProductsPage = lazy(() => import('@/pages/PublicProductsPage'));
const ProductDetailPage = lazy(() => import('@/pages/customer/ProductDetailPage'));
const CartPage = lazy(() => import('@/pages/customer/CartPage'));
const CheckoutPage = lazy(() => import('@/pages/customer/CheckoutPage'));
const MyOrdersPage = lazy(() => import('@/pages/customer/MyOrdersPage'));
const OrderTrackingPage = lazy(() => import('@/pages/customer/OrderTrackingPage'));
const ProfilePage = lazy(() => import('@/pages/customer/ProfilePage'));
const PaymentCallbackPage = lazy(() => import('@/pages/customer/PaymentCallbackPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));

// Farmer pages
const FarmerDashboard = lazy(() => import('@/pages/farmer/FarmerDashboard'));
const MyProductsPage = lazy(() => import('@/pages/farmer/MyProductsPage'));
const OrderManagementPage = lazy(() => import('@/pages/farmer/OrderManagementPage'));
const DispatchPage = lazy(() => import('@/pages/farmer/DispatchPage'));
const AnalyticsPage = lazy(() => import('@/pages/farmer/AnalyticsPage'));
const FarmerProfilePage = lazy(() => import('@/pages/farmer/ProfilePage'));

// Delivery agent pages
const DeliveryDashboard = lazy(() => import('@/pages/delivery/DeliveryDashboard'));
const ActiveDeliveriesPage = lazy(() => import('@/pages/delivery/ActiveDeliveriesPage'));
const DeliveryHistoryPage = lazy(() => import('@/pages/delivery/DeliveryHistoryPage'));
const EarningsPage = lazy(() => import('@/pages/delivery/EarningsPage'));
const RequestsPage = lazy(() => import('@/pages/delivery/RequestsPage'));
const DeliveryProfilePage = lazy(() => import('@/pages/delivery/ProfilePage'));

// Supplier pages
const SupplierDashboard = lazy(() => import('@/pages/supplier/SupplierDashboard'));
const CatalogPage = lazy(() => import('@/pages/supplier/CatalogPage'));
const SupplierOrdersPage = lazy(() => import('@/pages/supplier/OrdersPage'));
const SupplierProfilePage = lazy(() => import('@/pages/supplier/ProfilePage'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const AdminFarmerApprovalsPage = lazy(() => import('@/pages/admin/FarmerApprovalsPage'));
const AdminOrdersPage = lazy(() => import('@/pages/admin/OrdersPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/AnalyticsPage'));
const AdminCategoriesPage = lazy(() => import('@/pages/admin/CategoriesPage'));
const AdminUnitsPage = lazy(() => import('@/pages/admin/UnitsPage'));
const AdminProfilePage = lazy(() => import('@/pages/admin/ProfilePage'));
const AdminProductsPage = lazy(() => import('@/pages/admin/ProductsPage'));

// Settings page
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));

import { RouteError } from '@/components/common/RouteError';

export const router = createBrowserRouter([
  // Public routes (no theme)
  {
    errorElement: <RouteError />,
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            element: (
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/register',
            element: (
              <LazyWrapper>
                <RegisterPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/forgot-password',
            element: (
              <LazyWrapper>
                <ForgotPasswordPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/contact',
            element: (
              <LazyWrapper>
                <ContactPage />
              </LazyWrapper>
            ),
          },
        ],
      },
    ],
  },

  // Public accessible routes (with theme)
  {
    errorElement: <RouteError />,
    element: <PublicGuard />,
    children: [
      {
        element: <MarketLayout />,
        children: [
          {
            path: '/',
            element: (
              <LazyWrapper>
                <StoreHomePage />
              </LazyWrapper>
            ),
          },
          {
            path: '/products',
            element: (
              <LazyWrapper>
                <PublicProductsPage />
              </LazyWrapper>
            ),
          },
          // Guest shopping: browse, view details, build a cart and reach checkout
          // without an account. Checkout itself requires creating one.
          {
            path: '/products/:id',
            element: (
              <LazyWrapper>
                <ProductDetailPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/cart',
            element: (
              <LazyWrapper>
                <CartPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/checkout',
            element: (
              <LazyWrapper>
                <CheckoutPage />
              </LazyWrapper>
            ),
          },
          {
            path: '/payment/callback',
            element: (
              <LazyWrapper>
                <PaymentCallbackPage />
              </LazyWrapper>
            ),
          },
        ],
      },
    ],
  },


  // Protected routes (with theme)
  {
    errorElement: <RouteError />,
    element: <AuthGuard />,
    children: [
      {
        element: <MainLayout />,
        children: [
          // All role-based routes go here, wrapped by AppLayout
          {
            element: <AppLayout />,
            children: [
              // Settings route (all authenticated users)
              {
                path: '/settings',
                element: (
                  <LazyWrapper>
                    <SettingsPage />
                  </LazyWrapper>
                ),
              },
              // Customer & Shopping routes
              {
                element: <RoleGuard allowedRoles={[UserRole.CUSTOMER, UserRole.FARMER, UserRole.SUPPLIER]} />,
                children: [
                  {
                    path: '/home',
                    element: (
                      <LazyWrapper>
                        <HomePage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/favourites',
                    element: (
                      <LazyWrapper>
                        <FavouritesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/orders',
                    element: (
                      <LazyWrapper>
                        <MyOrdersPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/favourites',
                    element: (
                      <LazyWrapper>
                        <FavouritesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/orders/:id/track',
                    element: (
                      <LazyWrapper>
                        <OrderTrackingPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/profile',
                    element: (
                      <LazyWrapper>
                        <ProfilePage />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
              // Farmer routes
              {
                element: <RoleGuard allowedRoles={[UserRole.FARMER]} />,
                children: [
                  {
                    path: '/farmer',
                    element: (
                      <LazyWrapper>
                        <FarmerDashboard />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/products',
                    element: (
                      <LazyWrapper>
                        <MyProductsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/orders',
                    element: (
                      <LazyWrapper>
                        <OrderManagementPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/dispatch',
                    element: (
                      <LazyWrapper>
                        <DispatchPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/analytics',
                    element: (
                      <LazyWrapper>
                        <AnalyticsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/profile',
                    element: (
                      <LazyWrapper>
                        <FarmerProfilePage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/farmer/messages',
                    element: <div>Messages - Coming Soon</div>,
                  },
                ],
              },
              // Delivery agent routes
              {
                element: <RoleGuard allowedRoles={[UserRole.DELIVERY_AGENT]} />,
                children: [
                  {
                    path: '/delivery',
                    element: (
                      <LazyWrapper>
                        <DeliveryDashboard />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/delivery/requests',
                    element: (
                      <LazyWrapper>
                        <RequestsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/delivery/active',
                    element: (
                      <LazyWrapper>
                        <ActiveDeliveriesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/delivery/history',
                    element: (
                      <LazyWrapper>
                        <DeliveryHistoryPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/delivery/earnings',
                    element: (
                      <LazyWrapper>
                        <EarningsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/delivery/profile',
                    element: (
                      <LazyWrapper>
                        <DeliveryProfilePage />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
              // Supplier routes
              {
                element: <RoleGuard allowedRoles={[UserRole.SUPPLIER]} />,
                children: [
                  {
                    path: '/supplier',
                    element: (
                      <LazyWrapper>
                        <SupplierDashboard />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/supplier/catalog',
                    element: (
                      <LazyWrapper>
                        <CatalogPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/supplier/orders',
                    element: (
                      <LazyWrapper>
                        <SupplierOrdersPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/supplier/dispatch',
                    element: (
                      <LazyWrapper>
                        <DispatchPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/supplier/profile',
                    element: (
                      <LazyWrapper>
                        <SupplierProfilePage />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
              // Admin routes
              {
                element: <RoleGuard allowedRoles={[UserRole.ADMIN]} />,
                children: [
                  {
                    path: '/admin',
                    element: (
                      <LazyWrapper>
                        <AdminDashboard />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/users',
                    element: (
                      <LazyWrapper>
                        <AdminUsersPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/farmer-approvals',
                    element: (
                      <LazyWrapper>
                        <AdminFarmerApprovalsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/orders',
                    element: (
                      <LazyWrapper>
                        <AdminOrdersPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/analytics',
                    element: (
                      <LazyWrapper>
                        <AdminAnalyticsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/categories',
                    element: (
                      <LazyWrapper>
                        <AdminCategoriesPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/units',
                    element: (
                      <LazyWrapper>
                        <AdminUnitsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/products',
                    element: (
                      <LazyWrapper>
                        <AdminProductsPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: '/admin/profile',
                    element: (
                      <LazyWrapper>
                        <AdminProfilePage />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // Catch all
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);
